/**
 * Call-to-Action-Button in einer HTML-Box.
 *
 * Jans Auftrag 05.08.2026: „ein wichtiges Feature, welches sich Kunden wünschen
 * — einen CTA-Button. […] Der CTA Button kann immer in jeder HTML Box unten
 * platziert werden." Optik quasi identisch zum START-Button des
 * Startbildschirms, dazu Schriftauswahl und Ausrichtung; das Ziel öffnet
 * wahlweise ein neues Fenster oder ersetzt den Scrolly.
 *
 * WARUM DIE BESCHREIBUNG HIER LIEGT und nicht in einem der Editoren: Der Button
 * wird an DREI Stellen gezeichnet — Pro-Editor-Bühne, Vorschau, veröffentlichter
 * Scrolly — und in ZWEI Editoren eingestellt. Jede Stelle, die selbst
 * entscheidet, wann ein Button sichtbar ist oder wie sein Ziel aussieht, ist
 * eine Stelle, an der die Fassungen auseinanderlaufen. Das ist uns in dieser
 * Woche mehrfach passiert (Textformate, Box-Effekte, Titel).
 */

import { grundSchnitt } from "./schrift-staerke.js";

export type CtaTarget = "blank" | "self";
export type CtaAlign = "left" | "center" | "right";

export interface CtaConfig {
    /** Beschriftung. Leer + kein Bild = kein Button. */
    text?: string;
    /** Zieladresse. Ohne sie ist der Button nur Zierde — s. `resolveCta`. */
    url?: string;
    /** `blank` = neues Fenster, `self` = ersetzt den Scrolly. */
    target?: CtaTarget;
    /** Ausrichtung am unteren Rand der Box. */
    align?: CtaAlign;
    /** Eckenradius in px. */
    radius?: number;
    /** Stärke der Umrandung in px (Vorgabe 2, bis 03.10.2026: 3). */
    borderWidth?: number;
    /** Kontur — färbt Umrandung UND Beschriftung, wie beim START-Button. */
    borderColor?: { r: number; g: number; b: number; a: number } | null;
    /** Füllung. Fehlt sie, bleibt der Button durchsichtig. */
    fillColor?: { r: number; g: number; b: number; a: number } | null;
    /** Schriftfamilie; leer = Vorgabe der Box. */
    fontFamily?: string;
    /**
     * Schnitt der Beschriftung (100 … 900); fehlt = {@link CTA_SCHNITT} wie
     * bisher. Seit der Schriftwahl mit Schnitten in allen Menüs (07.10.2026).
     */
    fontWeight?: number;
    /** Bild STATT Button (PNG/JPEG/SVG). Ersetzt Beschriftung und Rahmen. */
    imageUrl?: string;
    /**
     * Eigenbreite der Bilddatei in Pixeln. Bei PNG/JPEG ist das die Breite, in
     * der das Bild in der Box erscheint — „px-Größe = Größe in der HTML-Box"
     * (Jans Regel 05.08.2026).
     */
    imageWidth?: number;
    /**
     * Ist es ein SVG? Vektoren haben keine verlässliche Eigengröße; ohne feste
     * Angabe füllen sie die ganze Box (Jans Test: das Logo lief über die halbe
     * Bühne). Sie bekommen deshalb eine Vorgabebreite.
     */
    imageIsVector?: boolean;
}

/** Aufbereiteter Button — oder `null`, wenn keiner gezeigt werden soll. */
export interface ResolvedCta {
    beschriftung: string;
    bildUrl: string;
    href: string;
    /** Fertiges `target`-Attribut. */
    target: "_blank" | "_self";
    /** Nur bei `_blank` nötig und dann Pflicht (Tabnabbing). */
    rel?: string;
    align: CtaAlign;
    /**
     * Breite des Bildes in Pixeln, oder `undefined` = Eigengröße überlassen.
     * Immer zusätzlich auf die Boxbreite gedeckelt, damit nichts überläuft.
     */
    bildBreite: number | undefined;
    radius: number;
    borderWidth: number;
    borderColor: CtaConfig["borderColor"];
    fillColor: CtaConfig["fillColor"];
    fontFamily: string;
    /** Schnitt der Beschriftung, immer gesetzt (Vorgabe {@link CTA_SCHNITT}). */
    fontWeight: number;
}

/** Schnitt der Knopf-Beschriftung ohne Wahl — SemiBold, wie seit jeher. */
export const CTA_SCHNITT = 600;

/**
 * Vorgabebreite für ein eingesetztes SVG.
 *
 * Jans Vorschlag 05.08.2026 („ich sag mal 512px"), und der Grund dahinter ist
 * belastbar: Ein SVG bringt oft keine nutzbare Eigengröße mit, der Browser
 * streckt es dann auf den verfügbaren Platz — im Test lief das Logo über die
 * halbe Bühne. Raster-Bilder brauchen das nicht, die haben eine echte Breite.
 */
export const CTA_SVG_BREITE = 512;

const ZAHL = (wert: unknown, ersatz: number): number =>
    typeof wert === "number" && Number.isFinite(wert) ? wert : ersatz;

const TEXT = (wert: unknown): string =>
    typeof wert === "string" ? wert.trim() : "";

/**
 * Eine eingetippte Adresse brauchbar machen.
 *
 * Jans Test 05.08.2026: Er trug „www.schwochow.de" ein, und der Button landete
 * auf `localhost:5174/editor/www.schwochow.de` → nicht gefunden. Kein Fehler des
 * Browsers: Ohne Schema ist das ein RELATIVER Pfad, und relativ heisst „im
 * Scrolly". Niemand tippt aber eine Domain, um im Scrolly zu bleiben.
 *
 * Deshalb: Fehlt das Schema, wird `https://` ergaenzt. Ausgenommen sind
 *   - vollstaendige Adressen jeder Art (http:, https:, mailto:, tel:)
 *   - absichtlich interne Ziele („/…" oder „#…")
 * Beides sind gueltige Angaben, die man nicht umbiegen darf.
 */
export function normalisiereCtaUrl(roh: string): string {
    const url = roh.trim();
    if (!url) return "";
    // Schema vorhanden? (RFC-Form: Buchstabe, dann Buchstaben/Ziffern/+-.)
    if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return url;
    // Absichtlich intern
    if (url.startsWith("/") || url.startsWith("#")) return url;
    return `https://${url}`;
}

/**
 * Einen Button aus der Slide-Angabe aufbereiten.
 *
 * `null` heisst: nichts zeichnen. Genau EINE Regel dafür, damit Editor und
 * Leser nicht unterschiedlich entscheiden:
 *
 *   - Ohne Beschriftung UND ohne Bild gibt es nichts zu zeigen.
 *   - Ohne Ziel ebenfalls nicht: Ein Knopf, der nichts tut, ist schlimmer als
 *     keiner — man klickt ihn, es passiert nichts, und niemand sieht dem
 *     Scrolly an, dass die Adresse fehlt. Im Editor weist die Maske darauf hin.
 */
export function resolveCta(cta: CtaConfig | null | undefined): ResolvedCta | null {
    if (!cta) return null;
    const beschriftung = TEXT(cta.text);
    const bildUrl = TEXT(cta.imageUrl);
    const href = normalisiereCtaUrl(TEXT(cta.url));
    if (!href) return null;
    if (!beschriftung && !bildUrl) return null;

    const target = cta.target === "self" ? "_self" : "_blank";
    return {
        beschriftung,
        bildUrl,
        href,
        target,
        // `noopener` ist bei _blank Pflicht: Ohne das kann die geöffnete Seite
        // über `window.opener` auf den Scrolly zugreifen.
        rel: target === "_blank" ? "noopener noreferrer" : undefined,
        align: cta.align === "left" || cta.align === "right" ? cta.align : "center",
        bildBreite: !bildUrl
            ? undefined
            : cta.imageIsVector
              ? CTA_SVG_BREITE
              : typeof cta.imageWidth === "number" && cta.imageWidth > 0
                ? cta.imageWidth
                : undefined,
        radius: ZAHL(cta.radius, 100),
        // Jans Vorgabe 03.10.2026: „Standardmäßig soll die Kontur überall
        // 2px haben" — START-Knopf und alle CTA-Knöpfe.
        borderWidth: ZAHL(cta.borderWidth, 2),
        borderColor: cta.borderColor ?? null,
        fillColor: cta.fillColor ?? null,
        fontFamily: TEXT(cta.fontFamily),
        fontWeight: grundSchnitt(cta.fontWeight, CTA_SCHNITT),
    };
}

/**
 * Fehlt dem Button nur die Adresse? Dann ist er ABSICHTLICH angelegt, aber noch
 * unvollständig — die Editor-Masken zeigen dafür einen Hinweis, statt den
 * Button kommentarlos verschwinden zu lassen.
 */
export function ctaZielFehlt(cta: CtaConfig | null | undefined): boolean {
    if (!cta) return false;
    const hatInhalt = TEXT(cta.text).length > 0 || TEXT(cta.imageUrl).length > 0;
    return hatInhalt && TEXT(cta.url).length === 0;
}
